import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../styles/MPProfile.css';

export default function MPProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quotes');
  
  useEffect(() => {
    // In a real app, this would fetch from your API
    const fetchProfile = async () => {
      setLoading(true);
      
      // Simulate API call with timeout
      setTimeout(() => {
        setProfile(generateMockProfile(id));
        setLoading(false);
      }, 1000);
    };
    
    fetchProfile();
  }, [id]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  if (loading) {
    return (
      <div className="mp-profile-page">
        <nav className="navbar">
          <div className="nav-container">
            <div className="logo">Aus<span>Politi</span>Match</div>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/policy-swipe/matches" className="nav-link">Matches</Link>
              <Link to="/faq" className="nav-link">FAQ</Link>
            </div>
          </div>
        </nav>
        
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading MP profile...</p>
        </div>
      </div>
    );
  }
  
  if (!profile) {
    return (
      <div className="mp-profile-page">
        <nav className="navbar">
          <div className="nav-container">
            <div className="logo">Aus<span>Politi</span>Match</div>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/policy-swipe/matches" className="nav-link">Matches</Link>
              <Link to="/faq" className="nav-link">FAQ</Link>
            </div>
          </div>
        </nav>
        
        <div className="error-container">
          <h2>MP Not Found</h2>
          <p>Sorry, we couldn't find an MP with that ID.</p>
          <Link to="/policy-swipe/matches" className="btn btn-primary">
            Return to Matches
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-profile-page">
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">Aus<span>Politi</span>Match</div>
          <div className="nav-links">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/policy-swipe/matches" className="nav-link">Matches</Link>
            <Link to="/faq" className="nav-link">FAQ</Link>
          </div>
        </div>
      </nav>
      
      <div className="profile-container">
        <div className="back-link">
          <Link to="/policy-swipe/matches" className="back-button">
            ← Back to Matches
          </Link>
        </div>
        
        <div className="profile-header">
          <div className="profile-image-container">
            <img src={profile.photoUrl} alt={profile.name} className="profile-image" />
            <div className="match-badge">{profile.matchScore}% Match</div>
          </div>
          
          <div className="profile-info">
            <h1>{profile.name}</h1>
            <p className="mp-party-info">{profile.party} for {profile.electorate}</p>
            <p className="mp-role">{profile.parliamentaryRole}</p>
            
            <div className="match-summary">
              <h3>Match Summary</h3>
              <div className="match-areas">
                {profile.policyAreas.map(area => (
                  <div className="match-area" key={area.name}>
                    <div className="area-label">{area.name}</div>
                    <div className="area-bar-container">
                      <div 
                        className="area-bar-fill" 
                        style={{ width: `${area.matchPercent}%` }}
                      ></div>
                    </div>
                    <div className="area-percent">{area.matchPercent}%</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="agreement-areas">
              <div className="agree-section">
                <h4>You Both Support</h4>
                <ul className="tag-list">
                  {profile.matchingIssues.map((issue, i) => (
                    <li key={i} className="support-tag">{issue}</li>
                  ))}
                </ul>
              </div>
              
              <div className="disagree-section">
                <h4>You Disagree On</h4>
                <ul className="tag-list">
                  {profile.differingIssues.map((issue, i) => (
                    <li key={i} className="oppose-tag">{issue}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="profile-tabs">
          <div className="tabs-header">
            <button 
              className={`tab-button ${activeTab === 'quotes' ? 'active' : ''}`}
              onClick={() => handleTabChange('quotes')}
            >
              Memorable Quotes
            </button>
            <button 
              className={`tab-button ${activeTab === 'voting' ? 'active' : ''}`}
              onClick={() => handleTabChange('voting')}
            >
              Voting Record
            </button>
            <button 
              className={`tab-button ${activeTab === 'background' ? 'active' : ''}`}
              onClick={() => handleTabChange('background')}
            >
              Background
            </button>
            <button 
              className={`tab-button ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => handleTabChange('analysis')}
            >
              Analysis
            </button>
          </div>
          
          <div className="tab-content">
            {activeTab === 'quotes' && (
              <div className="quotes-tab">
                <h2>Memorable Quotes</h2>
                {profile.keyQuotes.map((quote, i) => (
                  <blockquote key={i} className="quote-card">
                    <p className="quote-text">{quote.quote}</p>
                    <footer className="quote-footer">
                      <div className="quote-context">{quote.context}</div>
                      <div className="quote-date">{quote.date}</div>
                    </footer>
                  </blockquote>
                ))}
              </div>
            )}
            
            {activeTab === 'voting' && (
              <div className="voting-tab">
                <h2>Recent Voting Record</h2>
                <div className="voting-record">
                  {profile.votingHistory.map((vote, i) => (
                    <div key={i} className={`vote-item vote-${vote.vote}`}>
                      <div className="vote-icon">
                        {vote.vote === 'yes' ? '✓' : vote.vote === 'no' ? '✕' : '—'}
                      </div>
                      <div className="vote-details">
                        <h3 className="vote-title">{vote.bill_title}</h3>
                        <p className="vote-summary">{vote.summary}</p>
                        <div className="vote-meta">
                          <span className="vote-category">{vote.category}</span>
                          <span className="vote-date">{vote.date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {activeTab === 'background' && (
              <div className="background-tab">
                <h2>MP Background</h2>
                
                <div className="background-section">
                  <h3>Education</h3>
                  <p>{profile.background.education}</p>
                </div>
                
                <div className="background-section">
                  <h3>Professional History</h3>
                  <p>{profile.background.professional_history}</p>
                </div>
                
                <div className="background-section">
                  <h3>Parliamentary Roles</h3>
                  <p>{profile.background.parliament_roles}</p>
                </div>
                
                <div className="background-section">
                  <h3>First Elected</h3>
                  <p>{profile.background.first_elected}</p>
                </div>
              </div>
            )}
            
            {activeTab === 'analysis' && (
              <div className="analysis-tab">
                <h2>Speech Analysis</h2>
                
                <div className="analysis-section">
                  <h3>Communication Style</h3>
                  <p>{profile.speechAnalysis.communication_style}</p>
                </div>
                
                <div className="analysis-section">
                  <h3>Recurring Themes</h3>
                  <ul className="recurring-themes">
                    {profile.speechAnalysis.recurring_themes.map((theme, i) => (
                      <li key={i}>{theme}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="profile-footer">
          <div className="contact-section">
            <h3>Contact Information</h3>
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-label">Email:</div>
                <div className="contact-value">{profile.contactInfo.email}</div>
              </div>
              <div className="contact-item">
                <div className="contact-label">Phone:</div>
                <div className="contact-value">{profile.contactInfo.phone}</div>
              </div>
              <div className="contact-item">
                <div className="contact-label">Office:</div>
                <div className="contact-value">{profile.contactInfo.office}</div>
              </div>
            </div>
          </div>
          
          <div className="social-section">
            <h3>Social Media</h3>
            <div className="social-links">
              {Object.entries(profile.socialMedia).map(([platform, url]) => (
                <a 
                  key={platform} 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`social-link ${platform.toLowerCase()}`}
                >
                  {platform}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Function to generate mock profile data (would be replaced by API call in real app)
function generateMockProfile(id) {
  return {
    id: id,
    name: `MP ${id}`,
    party: ['Labor', 'Liberal', 'Greens', 'Independent'][Math.floor(Math.random() * 4)],
    electorate: ['Sydney', 'Melbourne', 'Brisbane', 'Perth'][Math.floor(Math.random() * 4)],
    photoUrl: `/api/placeholder/300/300`,
    matchScore: Math.floor(Math.random() * 26) + 70, // 70-95%
    parliamentaryRole: 'Shadow Minister for Environment and Water',
    
    policyAreas: [
      { name: 'Environment', matchPercent: 92 },
      { name: 'Economy', matchPercent: 78 },
      { name: 'Education', matchPercent: 85 },
      { name: 'Healthcare', matchPercent: 65 },
      { name: 'Defense', matchPercent: 45 }
    ],
    
    matchingIssues: [
      'Renewable Energy',
      'Climate Action',
      'School Funding',
      'Mental Health'
    ],
    
    differingIssues: [
      'Corporate Tax',
      'Defense Spending',
      'Immigration Policy'
    ],
    
    keyQuotes: [
      {
        quote: "We cannot afford to delay climate action any longer. The cost of inaction far exceeds the cost of transition to a sustainable future.",
        context: "Climate Change Debate",
        date: "March 15, 2023"
      },
      {
        quote: "Quality education is the foundation of opportunity. Every child, regardless of postcode or background, deserves access to excellent schools.",
        context: "Education Reform Bill",
        date: "June 22, 2023"
      },
      {
        quote: "Our healthcare system must be accessible to all Australians. We need to ensure that geography is not destiny when it comes to health outcomes.",
        context: "Healthcare Funding Debate",
        date: "September 8, 2023"
      },
      {
        quote: "Economic prosperity and environmental sustainability are not opposing forces - they are complementary goals that must be pursued together.",
        context: "Budget Reply Speech",
        date: "May 11, 2023"
      },
      {
        quote: "Mental health deserves the same attention and resources as physical health. The silent crisis affecting so many Australians needs our immediate action.",
        context: "Mental Health Bill",
        date: "November 30, 2023"
      }
    ],
    
    votingHistory: [
      {
        bill_title: "Climate Change Response Bill",
        summary: "Establish legally binding emissions reduction targets",
        vote: "yes",
        category: "Environment",
        date: "February 8, 2024"
      },
      {
        bill_title: "University Funding Reform",
        summary: "Reduce fees for STEM courses by 20%",
        vote: "yes",
        category: "Education",
        date: "December 5, 2023"
      },
      {
        bill_title: "Corporate Tax Reform",
        summary: "Implement global minimum corporate tax rate",
        vote: "no",
        category: "Economy",
        date: "November 18, 2023"
      },
      {
        bill_title: "Medicare Dental Expansion",
        summary: "Include basic dental services in Medicare",
        vote: "yes",
        category: "Healthcare",
        date: "October 30, 2023"
      },
      {
        bill_title: "Defense Spending Increase",
        summary: "Raise defense budget to 2.1% of GDP",
        vote: "no",
        category: "Defense",
        date: "September 12, 2023"
      },
      {
        bill_title: "Refugee Processing Reform",
        summary: "Expedite processing of asylum seekers",
        vote: "yes",
        category: "Immigration",
        date: "August 21, 2023"
      }
    ],
    
    background: {
      education: "Bachelor of Laws and Bachelor of Economics from the University of Sydney. Master of Public Policy from the Australian National University.",
      professional_history: "Environmental lawyer for 8 years, specializing in climate litigation. Former adviser to the Minister for Environment. Board member of the Climate Council.",
      parliament_roles: "Member of the Environment and Energy Committee. Chair of the Parliamentary Friends of Climate Action. Former Shadow Assistant Minister for the Environment.",
      first_elected: "2016 Federal Election"
    },
    
    speechAnalysis: {
      focus_areas: [
        "Climate Change",
        "Renewable Energy",
        "Education Reform",
        "Mental Health",
        "Regional Development"
      ],
      communication_style: "Employs evidence-based arguments with a focus on long-term outcomes. Frequently uses personal stories from constituents to illustrate policy impacts. Generally maintains a collaborative tone while being firm on core principles.",
      recurring_themes: [
        "Intergenerational equity in policy decisions",
        "Balance between economic and environmental concerns",
        "Need for bipartisan approach to major challenges",
        "Importance of science-based policy making"
      ]
    },
    
    contactInfo: {
      email: `mp${id}@aph.gov.au`,
      phone: "(02) 6277 7890",
      office: "Parliament House, Canberra ACT 2600"
    },
    
    socialMedia: {
      Twitter: "https://twitter.com/mp",
      Facebook: "https://facebook.com/mp",
      Instagram: "https://instagram.com/mp",
      LinkedIn: "https://linkedin.com/in/mp"
    }
  };
}